import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import { connect } from 'react-redux';
import { selectSchool } from '../actions';
import store from '../store';

import * as d3 from 'd3';
import Tooltip from './tooltip';
import { format } from '../modules/helpers';
import filter from '../modules/filter-data';

let svg;
let tooltip;

function makeClassroomSpace(schools_data, cluster, chartName) {
  const width = 1000;
  const height = 600;
  const stack_gap = 30;
  const margin = { top: 50, right: 0, bottom: 50, left: 0 };

  const data = filter.classSpace(schools_data);

  const scaleFactor = data.length < 5 ? 0 : 0.5 * data.length; // This is a bit crude and might not scale in all case but works for data we hacve
  let elm;
  let card;
  let aside;
  let main;
  let slice;
  let average;
  let activeSchool;

  var classroomSpaceScale = d3
    .scaleLinear()
    .domain([
      0,
      d3.max(data.map(d => Math.sqrt(d.classroomspace) + scaleFactor)),
    ])
    .range([0, height - margin.top]);

  const calculateClassroomSpace = d => {
    return classroomSpaceScale(Math.sqrt(d.classroomspace)) / 2;
  };

  const invertClassroomSpace = d => {
    return classroomSpaceScale.invert(d.classroomspace) * 2;
  };

  function calculateAverage() {
    let sum = 0;
    data.forEach(obj => {
      if (obj.isActive) {
        activeSchool = obj;
      }
      sum += parseFloat(obj.classroomspace);
    });
    average = sum / data.length;
  }

  function initialiseElements() {
    elm = document.createElement('div');
    aside = d3.select(elm).append('div').attr('class', 'aside');
    main = d3
      .select(elm)
      .append('div')
      .attr('class', 'main')
      .attr('aria-hidden', 'true');

    // For clusters with lots of schools, we need to make the viewbox bigger
    let viewBoxY1 = 0;
    let viewBoxY2 = height;

    const defaultSchools = 14; // 14 schools fit by default
    const outOfSightSchools = schools_data.length - defaultSchools;
    if (outOfSightSchools > 0) {
      let extraHeight = height / defaultSchools * outOfSightSchools;
      extraHeight = extraHeight + 100; // just breathing room
      viewBoxY1 = 0 - extraHeight * 0.5;
      viewBoxY2 = height + extraHeight * 0.5;
    }

    svg = main
      .append('svg')
      .attr('id', chartName)
      .attr('viewBox', `0 ${viewBoxY1} ${width} ${viewBoxY2}`)
      .attr('class', 'cluster-chart classroomspace-chart');
  }

  function setupToolTip() {
    const content = `<div class="tooltip-content-wrapper">
      <div class="tooltip-content">
      <div>
        <p class="tooltip-title"></p>
        <p class="tooltip-number"></p>
        <p class="a11y">Press enter to navigate to this school's page or tab to select another school.</p>
      </div>
      </div>
    </div>`;

    tooltip = new Tooltip(content);
    main.node().appendChild(tooltip.elm);
  }

  const handleClick = function(d) {
    if (d3.event) d3.event.stopPropagation();

    store.dispatch(
      selectSchool({
        [chartName]: {
          selectedSchool: d,
          showTooltip: true,
        },
      })
    );
  };

  function drawAside() {
    if (activeSchool) {
      card = aside.append('div').attr('class', 'card--highlight');

      card.append('p').attr('class', 'card-title').text(activeSchool.name);

      card
        .append('p')
        .attr('class', 'big-number')
        .text(`${Math.round(activeSchool.classroomspace * 100) / 100}m²`);
    }

    card = aside.append('div').attr('class', 'card card-average');

    card
      .append('p')
      .attr('class', 'card-title')
      .text(`Average classroom space for ${cluster} schools group`);

    card
      .append('p')
      .attr('class', 'big-number')
      .text(`${Math.round(average * 100) / 100}m²`);

    // card.append('p')
    //   .attr('class', 'card-title')
    //   .text('per student')
  }

  function drawChart() {
    const averageClassroomSpace = calculateClassroomSpace({
      classroomspace: average,
    });
    const defs = svg.append('defs');
    const svgContainer = svg
      .append('g')
      .attr('transform', `translate(${margin.left} ${margin.top})`)
      .append('g')
      .attr('transform', () => {
        const top =
          height -
          margin.bottom -
          margin.top -
          stack_gap * data.length -
          calculateClassroomSpace(data[data.length - 1]) / 2;
        return `translate(${10} ${top})`;
      })
      .append('g')
      .attr('transform', 'matrix(0.866, 0.5, -0.866, 0.5, 0, 0)');

    // An alternative method results in a smaller isometric projection
    // Can someone tell me why the translate is slightly different between the two methods?

    // .append("g")
    // .attr("transform", "translate(" + (stack_gap * (data.length-1)) + " " + (height - (stack_gap * (data.length-1)) ) +")")
    // .attr("transform", "scale(1, 0.5)")
    // .append("g")
    // .attr("transform", "rotate(45)")

    // Make a copy of the data and splice in the avergage for cluster view
    const copy = data.map(d => d);
    if (!activeSchool) {
      let j = 0;
      copy.forEach((d, i) => {
        j = d.classroomspace > average ? j : i;
      });
      copy.splice(j + 1, 0, { classroomspace: average, isAverage: true });
    }

    // Slice is in correct order use d
    slice = defs
      .selectAll('g')
      .data(copy)
      .enter()
      .append('g')
      .attr('id', (d, i) => `slice-classroom-${d.uid}`)
      .attr('data-name', (d, i) => `${d.name}`)
      .on('keydown', function(d, i) {
        if (d.isAverage) return;
        const e = d3.event;
        const keys = {
          SPACE: 32,
          ENTER: 13,
        };
        if (
          e.type === 'click' ||
          e.keyCode === keys.SPACE ||
          e.keyCode === keys.ENTER
        ) {
          d3.selectAll('use').classed('nmbm', false);
          d3.select(`#use-classroom-${d.uid}`).classed('nmbm', true);
          handleClick(d);
        }
      });

    slice
      .attr('transform', (d, i) => {
        const sqd = calculateClassroomSpace(d);
        const y = i * stack_gap - sqd;
        const x = i * stack_gap;
        return `translate(${x} ${y})`;
      })
      .attr('class', d => `slice ${d.isActive ? 'is-active' : ''}`)
      .attr('tabindex', 0);

    slice
      .append('rect')
      .attr('class', d => {
        return d.isAverage ? 'average' : 'rect';
      })
      .attr('width', calculateClassroomSpace)
      .attr('height', calculateClassroomSpace);

    slice
      .filter(d => !d.isAverage)
      .append('rect')
      .attr('class', 'left-side')
      .attr('width', calculateClassroomSpace)
      .attr('height', 5)
      .attr('transform', d => {
        const sqd = calculateClassroomSpace(d);
        return `skewX(45) rotate(180 0 0) translate(0 ${-(sqd + 5)})`;
      });

    slice
      .filter(d => !d.isAverage)
      .append('rect')
      .attr('class', 'right-side')
      .attr('height', calculateClassroomSpace)
      .attr('width', 5)
      .attr('transform', d => {
        const sqd = calculateClassroomSpace(d);
        return `skewY(45) rotate(0 0 ${sqd})  translate(${sqd} ${-sqd})`;
      });

    slice.each(function(d) {
      if (d.isActive) {
        d3
          .select(this)
          .append('rect')
          .attr('class', 'average')
          .attr('width', () => {
            return averageClassroomSpace;
          })
          .attr('height', () => {
            return averageClassroomSpace;
          })
          .attr('pointer-events', 'none')
          .attr('transform', d => {
            const sqd = calculateClassroomSpace(d) - averageClassroomSpace;
            const y = sqd;
            const x = 0;
            return `translate(${x} ${y})`;
          });
      }
    });

    // Use is in reverse correct order use copy[copy.length - i - 1]
    svgContainer
      .selectAll('use')
      .data(copy)
      .enter()
      .append('use')
      .attr('class', (d, i) => {
        return copy[copy.length - i - 1].isActive ? 'is-active' : null;
      })
      .attr('id', (d, i) => `use-classroom-${copy[copy.length - i - 1].uid}`)
      .attr('data-name', (d, i) => `${copy[copy.length - i - 1].name}`)
      .attr(
        'xlink:href',
        (d, i) => `#slice-classroom-${copy[copy.length - i - 1].uid}`
      )
      .attr('tabindex', -1)
      .on('mouseover', (d, i) => {
        const node = d3
          .select(`#slice-classroom-${copy[copy.length - i - 1].uid}`)
          .node();
        if (node.focus) node.focus();
      })
      .on('mouseout', (d, i) => {
        const node = d3
          .select(`#slice-classroom-${copy[copy.length - i - 1].uid}`)
          .node();
        if (node.blur) node.blur();
      })
      .on('click', function(d, i) {
        if (copy[copy.length - i - 1].isAverage) return;
        d3.selectAll('use').classed('nmbm', false);
        d3.select(this).classed('nmbm', true);
        handleClick(copy[copy.length - i - 1]);
      });
  }

  function drawKey() {
    const keyArea = invertClassroomSpace({ classroomspace: 20 });
    const keySize = 20;
    const key = svg.append('g');

    key.attr('class', 'key').attr('transform', (d, i) => {
      const y = height - margin.bottom / 2;
      const x = margin.left;
      return `translate(${x} ${y})`;
    });

    const perspectiveContainer = key
      .append('g')
      .attr('class', 'slice')
      .attr('transform', `translate(${keySize} ${-2.5})`)
      .append('g')
      .attr('transform', 'matrix(0.866, 0.5, -0.866, 0.5, 0, 0)');

    perspectiveContainer
      .append('rect')
      .attr('class', 'rect')
      .attr('width', keySize)
      .attr('height', keySize);

    perspectiveContainer
      .append('rect')
      .attr('class', 'left-side')
      .attr('width', keySize)
      .attr('height', 5)
      .attr(
        'transform',
        d => `skewX(45) rotate(180 0 0) translate(0 ${-(keySize + 5)})`
      );

    perspectiveContainer
      .append('rect')
      .attr('class', 'right-side')
      .attr('height', keySize)
      .attr('width', 5)
      .attr(
        'transform',
        d =>
          `skewY(45) rotate(0 0 ${keySize})  translate(${keySize} ${-keySize})`
      );

    key
      .append('text')
      .text(`= ${format(keyArea * 100, 1)}cm² classroom space`)
      .attr('transform', (d, i) => {
        const y = 17;
        const x = margin.left + keySize * 1.75 + 10;
        return `translate(${x} ${y})`;
      });
  }

  calculateAverage();
  initialiseElements();
  setupToolTip();
  main
    .append('div')
    .style('font-size', '11px')
    .text('Data subject to change.*');
  drawAside();
  drawChart();
  drawKey();

  elm.classList.add('chart');

  return elm;
}

class AvgClassroomSpace extends Component {
  wrapChart() {
    this.container = findDOMNode(this);
    while (this.container.firstChild) {
      this.container.removeChild(this.container.firstChild);
    }
    const { data } = this.props;
    const chart = makeClassroomSpace(
      data.schools,
      data.name,
      this.props.chartName
    );
    this.container.appendChild(chart);
  }

  componentDidMount() {
    this.wrapChart();
  }

  componentDidUpdate() {
    const { selectedSchool, showTooltip } = this.props.ui;
    if (showTooltip && selectedSchool) {
      svg.selectAll('.nmbm').classed('nmbm', false);
      svg.select(`#use-classroom-${selectedSchool.uid}`).classed('nmbm', true);

      tooltip.onClick(function() {
        if (window.parentIFrame) {
          window.parentIFrame.sendMessage({
            type: 'change:school',
            school: selectedSchool.uid,
          });
        } else {
          window.location.search = `school=${selectedSchool.uid}`;
        }
      });

      tooltip.elm.classList.add('tooltip-arrow');
      tooltip.elm.classList.remove('tooltip-active');

      if (selectedSchool.isActive) {
        tooltip.elm.classList.remove('tooltip-arrow');
        tooltip.elm.classList.add('tooltip-active');
      }

      tooltip.show();

      tooltip.elm.querySelector('.tooltip-title').innerHTML =
        selectedSchool.name;
      tooltip.elm.querySelector('.tooltip-number').innerHTML = `${format(
        selectedSchool.classroomspace
      )}m²`;

      const bb = svg
        .select(`#use-classroom-${selectedSchool.uid}`)
        .node()
        .getBoundingClientRect();
      const scrollX = window.scrollX || document.documentElement.scrollLeft;
      const scrollY = window.scrollY || document.documentElement.scrollTop;

      const top =
        bb.top + bb.height / 2 + scrollY - tooltip.elm.clientHeight - 20;
      const left =
        bb.left + bb.width + scrollX - tooltip.elm.clientWidth / 2 - 10;

      tooltip.move(top, left);
    }
  }

  render() {
    return <div />;
  }
}

const mapStateToProps = (state, ownProps) => ({
  ui: state.ui[ownProps.chartName],
});

const mapDispatchToProps = dispatch => ({
  selectSchool: p => dispatch(selectSchool(p)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AvgClassroomSpace);



// WEBPACK FOOTER //
// ./src/components/classroom-space.js